import { useEffect, useState } from "react";
import { Platform } from "react-native";

/**
 * Singleton class to track whether the user is navigating via keyboard (Tab key)
 * This allows showing focus indicators only during keyboard navigation,
 * not when clicking with mouse/touch.
 */
class TabbingObservable {
  private _isTabbing = false;
  private _subscribers: Set<(value: boolean) => void> = new Set();
  private _isInitialized = false;

  constructor() {
    this._initialize();
  }

  private _initialize() {
    if (
      this._isInitialized ||
      Platform.OS !== "web" ||
      typeof document === "undefined"
    ) {
      return;
    }

    this._isInitialized = true;

    document.addEventListener(
      "keydown",
      (e: KeyboardEvent) => {
        if (e.key === "Tab") {
          this._setIsUserTabbing(true);
        }
      },
      true
    );

    document.addEventListener(
      "mousedown",
      () => {
        this._setIsUserTabbing(false);
      },
      true
    );

    document.addEventListener(
      "touchstart",
      () => {
        this._setIsUserTabbing(false);
      },
      true
    );
  }

  private _setIsUserTabbing(value: boolean) {
    if (this._isTabbing !== value) {
      this._isTabbing = value;
      this._subscribers.forEach((subscriber) => subscriber(value));
    }
  }

  subscribe(callback: (value: boolean) => void): () => void {
    this._subscribers.add(callback);
    callback(this._isTabbing);

    return () => {
      this._subscribers.delete(callback);
    };
  }

  getIsTabbing(): boolean {
    return this._isTabbing;
  }
}

const tabbingObservable = new TabbingObservable();

/**
 * Hook to detect if the user is currently navigating via keyboard (Tab key).
 * Use this to conditionally show focus indicators only during keyboard navigation.
 *
 * @example
 * const isTabbing = useTabbing();
 *
 * <Pressable
 *   style={[
 *     styles.button,
 *     focused && isTabbing && styles.focusRing
 *   ]}
 * />
 *
 * @returns {boolean} True if user is using keyboard navigation, false otherwise
 */
export function useTabbing(): boolean {
  const [isTabbing, setIsTabbing] = useState(false);

  useEffect(() => {
    if (Platform.OS !== "web") {
      return undefined;
    }

    const unsubscribe = tabbingObservable.subscribe(setIsTabbing);
    return unsubscribe;
  }, []);

  return isTabbing;
}

export default useTabbing;
